// Rasi (Moon sign) and Nakshatra (with pada) from the date, time and place of birth.
//
// The Moon's position uses the main terms of the lunar theory in Meeus, "Astronomical
// Algorithms" ch. 47 (accurate to about 0.01°), and the Lahiri (Chitrapaksha) ayanamsa for the
// sidereal zodiac. The birth time is local time at the place of birth; its UTC offset converts
// it to universal time. A Nakshatra spans 13°20', so results within a few arc-minutes of a
// boundary are flagged for checking against a traditional panchangam.

// In zodiac order, spelled as the profile's lists of values store them (horoscope_star /
// horoscope_rasi), so a result maps straight onto the dropdowns
export const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Moola', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta',
  'Shatabhisha', 'Purva Bhadra', 'Uttara Bhadra', 'Revati',
];
export const RASIS = [
  'Mesha', 'Rishabha', 'Mithuna', 'Kataka', 'Simha', 'Kanya',
  'Thula', 'Vrischika', 'Dhanus', 'Makara', 'Kumbha', 'Meena',
];

const rad = (d) => (d * Math.PI) / 180;
const norm = (d) => ((d % 360) + 360) % 360;

// Periodic terms for the Moon's longitude: [D, M, M', F, coefficient in 1e-6 degrees]
const TERMS = [
  [0, 0, 1, 0, 6288774], [2, 0, -1, 0, 1274027], [2, 0, 0, 0, 658314], [0, 0, 2, 0, 213618],
  [0, 1, 0, 0, -185116], [0, 0, 0, 2, -114332], [2, 0, -2, 0, 58793], [2, -1, -1, 0, 57066],
  [2, 0, 1, 0, 53322], [2, -1, 0, 0, 45758], [0, 1, -1, 0, -40923], [1, 0, 0, 0, -34720],
  [0, 1, 1, 0, -30383], [2, 0, 0, -2, 15327], [0, 0, 1, 2, -12528], [0, 0, 1, -2, 10980],
  [4, 0, -1, 0, 10675], [0, 0, 3, 0, 10034], [4, 0, -2, 0, 8548], [2, 1, -1, 0, -7888],
  [2, 1, 0, 0, -6766], [1, 0, -1, 0, -5163], [1, 1, 0, 0, 4987], [2, -1, 1, 0, 4036],
  [2, 0, 2, 0, 3994], [4, 0, 0, 0, 3861], [2, 0, -3, 0, 3665], [0, 1, -2, 0, -2689],
  [2, 0, -1, 2, -2602], [2, -1, -2, 0, 2390], [1, 0, 1, 0, -2348], [2, -2, 0, 0, 2236],
  [0, 1, 2, 0, -2120], [0, 2, 0, 0, -2069],
];

// Julian Day of a UTC date-time
function julianDay(y, m, d, hoursUtc) {
  if (m <= 2) { y -= 1; m += 12; }
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + d + hoursUtc / 24 + b - 1524.5;
}

// The Moon's geocentric (tropical) longitude in degrees at Julian Day jd
export function moonLongitude(jd) {
  const T = (jd - 2451545.0) / 36525;
  const Lp = 218.3164477 + 481267.88123421 * T - 0.0015786 * T * T + (T ** 3) / 538841 - (T ** 4) / 65194000;
  const D = 297.8501921 + 445267.1114034 * T - 0.0018819 * T * T + (T ** 3) / 545868 - (T ** 4) / 113065000;
  const M = 357.5291092 + 35999.0502909 * T - 0.0001536 * T * T + (T ** 3) / 24490000;
  const Mp = 134.9633964 + 477198.8675055 * T + 0.0087414 * T * T + (T ** 3) / 69699 - (T ** 4) / 14712000;
  const F = 93.2720950 + 483202.0175233 * T - 0.0036539 * T * T - (T ** 3) / 3526000 + (T ** 4) / 863310000;
  const E = 1 - 0.002516 * T - 0.0000074 * T * T;
  let sum = 0;
  for (const [d, m, mp, f, c] of TERMS) {
    const e = Math.abs(m) === 1 ? E : Math.abs(m) === 2 ? E * E : 1;
    sum += c * e * Math.sin(rad(d * D + m * M + mp * Mp + f * F));
  }
  const A1 = 119.75 + 131.849 * T;
  sum += 3958 * Math.sin(rad(A1)) + 1962 * Math.sin(rad(Lp - F)) + 318 * Math.sin(rad(53.09 + 479264.29 * T));
  return norm(Lp + sum / 1e6);
}

// Lahiri ayanamsa in degrees (≈ 23°51' at J2000, precessing 50.29" a year)
export function lahiriAyanamsa(jd) {
  const T = (jd - 2451545.0) / 36525;
  return 23.85306 + 1.39697 * T + 0.0003086 * T * T;
}

// "+05:30" → 5.5 (hours east of UTC)
export function parseUtcOffset(offset) {
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(offset || '');
  if (!m) return null;
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) + Number(m[3]) / 60);
}

// { rasi, nakshatra, pada, siderealLongitude, nearBoundary } — dob "YYYY-MM-DD", birthTime "HH:MM"
// (local time at the place of birth), utcOffset "+05:30". Without a birth time, noon is assumed
// and timeKnown is false (the Moon moves about 13° a day, so the Nakshatra may differ).
export function calcRasiNakshatra(dob, birthTime, utcOffset = '+05:30') {
  if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) return null;
  const offset = parseUtcOffset(utcOffset) ?? 5.5;
  const [y, m, d] = dob.split('-').map(Number);
  const [h, min] = birthTime ? birthTime.split(':').map(Number) : [12, 0];
  // Local time minus the offset is UTC; TT − UT (≈ a minute) is too small to matter here
  const jd = julianDay(y, m, d, h + min / 60 - offset);
  const sidereal = norm(moonLongitude(jd) - lahiriAyanamsa(jd));
  const span = 360 / 27;
  const nakIdx = Math.floor(sidereal / span);
  const within = sidereal - nakIdx * span;
  const pada = Math.floor(within / (span / 4)) + 1;
  // Within 10 arc-minutes (about 20 minutes of birth time) of a Nakshatra boundary
  const nearBoundary = within < 1 / 6 || span - within < 1 / 6;
  return {
    rasi: RASIS[Math.floor(sidereal / 30)],
    nakshatra: NAKSHATRAS[nakIdx],
    pada,
    siderealLongitude: sidereal,
    nearBoundary,
    timeKnown: Boolean(birthTime),
  };
}
